import { useCallback, useEffect, useRef } from 'react';

const VIEWPORT_MARGIN = 12;

export const useClampMenuHeight = () => {
	const nodeRef = useRef<HTMLDivElement | null>(null);

	const updateHeight = useCallback(() => {
		const node = nodeRef.current;
		if (!node) return;

		const rect = node.getBoundingClientRect();
		const vv = window.visualViewport;

		// Use visualViewport measurements if supported, fallback to window.innerHeight
		const viewportHeight = vv ? vv.height : window.innerHeight;
		const viewportOffsetTop = vv ? vv.offsetTop : 0;

		// Calculate node top relative to the VISIBLE viewport (accounting for virtual keyboard)
		const nodeTopRelativeToVisualViewport = rect.top - viewportOffsetTop;

		// Remaining visible space below the dropdown top
		const availableInViewport = viewportHeight - nodeTopRelativeToVisualViewport - VIEWPORT_MARGIN;

		node.style.setProperty('--dropdown-available-height', `${Math.max(0, availableInViewport)}px`);
	}, []);

	useEffect(() => {
		updateHeight();

		// Event listeners
		window.addEventListener('resize', updateHeight);
		window.addEventListener('scroll', updateHeight, true);

		const vv = window.visualViewport;
		if (vv) {
			vv.addEventListener('resize', updateHeight);
			vv.addEventListener('scroll', updateHeight);
		}

		return () => {
			window.removeEventListener('resize', updateHeight);
			window.removeEventListener('scroll', updateHeight, true);

			if (vv) {
				vv.removeEventListener('resize', updateHeight);
				vv.removeEventListener('scroll', updateHeight);
			}
		};
	}, [updateHeight]);

	return useCallback(
		(node: HTMLDivElement | null) => {
			nodeRef.current = node;
			if (node) updateHeight();
		},
		[updateHeight]
	);
};
